const R = 6371e3; // R is earth’s radius
const PI = 3.1415926535;
const GEO_CACHE_KEY = "omint_geo_location_cache";
const GEO_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutos

type GeoCoords = { latitude: number; longitude: number };

let memoryCache: (GeoCoords & { cachedAt: number }) | null = null;
let pendingLocationRequest: Promise<GeoCoords> | null = null;

function toRadians(val) {
  return (val / 180) * PI;
}

function isCacheValid(cachedAt: number) {
  return Date.now() - cachedAt < GEO_CACHE_TTL_MS;
}

function readPersistedCache(): (GeoCoords & { cachedAt: number }) | null {
  try {
    const raw = sessionStorage.getItem(GEO_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.latitude !== "number" ||
      typeof parsed?.longitude !== "number" ||
      typeof parsed?.cachedAt !== "number" ||
      !isCacheValid(parsed.cachedAt)
    ) {
      sessionStorage.removeItem(GEO_CACHE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function persistCache(coords: GeoCoords) {
  const payload = { ...coords, cachedAt: Date.now() };
  memoryCache = payload;
  try {
    sessionStorage.setItem(GEO_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage pode falhar em modo privado; memória ainda funciona
  }
}

export function FindDistance({ lat1, lon1, lat2, lon2 }) {
  const lat1radians = toRadians(lat1);
  const lat2radians = toRadians(lat2);
  const latRadians = toRadians(lat2 - lat1);
  const lonRadians = toRadians(lon2 - lon1);

  const a =
    Math.sin(latRadians / 2) * Math.sin(latRadians / 2) +
    Math.cos(lat1radians) * Math.cos(lat2radians) * Math.sin(lonRadians / 2) * Math.sin(lonRadians / 2);

  const d = (R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))) / 1000; // KM
  return Math.round(d * 100) / 100; // KM with 2 digits precision (0.00 KM)
}

function ShowPosition(done) {
  return function (position) {
    return done({
      longitude: position.coords.longitude,
      latitude: position.coords.latitude
    });
  };
}

function LocationError(done) {
  return function (error) {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        globalThis.window.$log.message("User denied the request for Geolocation");
        break;
      case error.POSITION_UNAVAILABLE:
        globalThis.window.$log.message("Location information is unavailable");
        break;
      case error.TIMEOUT:
        globalThis.window.$log.message("The request to get user location timed out");
        break;
      case error.UNKNOWN_ERROR:
        globalThis.window.$log.message("An unknown error occurred.");
        break;
    }
    return done();
  };
}

function requestBrowserGeolocation(): Promise<GeoCoords> {
  return new Promise(function (res, rej) {
    const a = ShowPosition(res);
    const b = LocationError(rej);
    // maximumAge permite ao browser reutilizar uma posição recente do SO
    navigator.geolocation.getCurrentPosition(a, b, {
      timeout: 15000,
      maximumAge: GEO_CACHE_TTL_MS,
      enableHighAccuracy: false
    });
  });
}

export async function GetLocation() {
  if (!navigator.geolocation) {
    throw new Error("Geolocation is unavailable");
  }

  if (memoryCache && isCacheValid(memoryCache.cachedAt)) {
    return { latitude: memoryCache.latitude, longitude: memoryCache.longitude };
  }

  const persisted = readPersistedCache();
  if (persisted) {
    memoryCache = persisted;
    return { latitude: persisted.latitude, longitude: persisted.longitude };
  }

  // Evita várias chamadas GPS em paralelo (ex.: filtro + infinite scroll)
  if (pendingLocationRequest) {
    return pendingLocationRequest;
  }

  pendingLocationRequest = requestBrowserGeolocation()
    .then(coords => {
      persistCache(coords);
      globalThis.window.$log.message({ coords });
      return coords;
    })
    .finally(() => {
      pendingLocationRequest = null;
    });

  return pendingLocationRequest;
}

export async function CalculateDistance(originalData, latitudeName, longitudeName) {
  const coords: any = await GetLocation().catch(() => null);
  let data = originalData;
  if (coords) {
    data = await Promise.all(
      originalData.map(async d => {
        let Distancia = undefined;
        if (d[latitudeName] !== "" && d[longitudeName] !== "") {
          Distancia = FindDistance({
            lat1: coords.latitude,
            lon1: coords.longitude,
            lat2: d[latitudeName],
            lon2: d[longitudeName]
          });
        }
        return { ...d, Distancia };
      })
    );
    return data.sort((a, b) => {
      if (a.Distancia === undefined && b.Distancia === undefined) return 3;
      if (a.Distancia === undefined && b.Distancia !== undefined) return 2;
      if (a.Distancia !== undefined && b.Distancia === undefined) return -2;
      if (a.Distancia < b.Distancia) return -1;
      if (a.Distancia > b.Distancia) return 1;
      return 0;
    });
  } else {
    return originalData;
  }
}
